import { AlertCircle, CheckCircle2, Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';

/** Campo com rótulo, dica opcional e mensagem de erro ligada ao campo (aria-describedby). */
export function FormField({
  id,
  label,
  error,
  hint,
  action,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: ReactNode;
  /** Algo ao lado do rótulo (ex.: link "Esqueci minha senha"). */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        {action}
      </div>
      {children}
      {hint ? <div id={`${id}-dica`}>{hint}</div> : null}
      {error ? (
        <p id={`${id}-erro`} className="text-sm font-medium text-status-red-fg">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Atributos de acessibilidade de um campo conforme erro e dica. */
export function fieldA11y(id: string, error?: string, hasHint = false) {
  const describedBy = [error ? `${id}-erro` : null, hasHint ? `${id}-dica` : null].filter(Boolean);
  return {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy.length ? describedBy.join(' ') : undefined,
  } as const;
}

/** Campo de senha com mostrar/ocultar. */
export function PasswordInput({ className, ...props }: Omit<ComponentProps<typeof Input>, 'type'>) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={visible ? 'text' : 'password'} className={cn('h-11 pr-11', className)} />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="absolute top-1/2 right-1.5 -translate-y-1/2 text-muted-foreground"
        aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        aria-pressed={visible}
        aria-controls={props.id}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
      </Button>
    </div>
  );
}

/** Aviso dentro de formulário: erro, conta bloqueada ou sucesso. */
export function FormAlert({
  tone,
  title,
  children,
  id,
}: {
  tone: 'error' | 'locked' | 'success';
  title?: string;
  children: ReactNode;
  id?: string;
}) {
  const Icon = tone === 'success' ? CheckCircle2 : tone === 'locked' ? LockKeyhole : AlertCircle;
  return (
    <div
      id={id}
      role={tone === 'success' ? 'status' : 'alert'}
      className={cn(
        'flex gap-3 rounded-md p-3 text-sm',
        tone === 'error' && 'bg-status-red-bg text-status-red-fg',
        tone === 'locked' && 'bg-status-yellow-bg text-status-yellow-fg',
        tone === 'success' && 'bg-status-green-bg text-status-green-fg',
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div>
        {title ? <p className="font-bold">{title}</p> : null}
        <div>{children}</div>
      </div>
    </div>
  );
}
